import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import type {MutableRefObject} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import {RootState} from 'setup'
import {requestOnboardingAiAllocation} from 'setup/redux/Actions/Onboarding/teacherAllocationAi'

import type {WizardData} from '../index'
import type {
  AiAllocationAction,
  AiAllocationContext,
  AiAllocationRequest,
  AiAllocationResponse,
  AiDisambiguationOption,
  AllocationScope,
  ConversationTurn,
  LessonPlanningData,
} from '../types/teacherAllocationAi'
import {buildAllocationContext} from '../utils/buildAllocationContext'
import {buildDisambiguationFollowUp} from '../utils/buildDisambiguationFollowUp'
import {cloneLessonPlanningData} from '../utils/cloneLessonPlanningData'
import {
  getPreviewSlotKeys,
  mapActionsToLessonPlanningData,
} from '../utils/mapActionsToLessonPlanningData'
import {
  buildMockAllocationResponse,
  getEffectiveAllocationActions,
  hasAllocationConfirmation,
  normalizeAllocationAiResponse,
} from '../utils/normalizeAllocationAiResponse'
import {
  MAX_UNDO_SNAPSHOTS,
  buildAllocationAiSessionKey,
  clearAllocationAiSession,
  readAllocationAiSession,
  writeAllocationAiSession,
} from '../utils/teacherAllocationAiSession'

export type UseTeacherAllocationAiParams = {
  manualDataRef: MutableRefObject<WizardData['manualData']>
  lessonPlanningData: LessonPlanningData
  setLessonPlanningData: React.Dispatch<React.SetStateAction<LessonPlanningData>>
  normalizeBranchName: (branch?: string) => string
  getSubjectsForClass: (classItem: any) => string[]
  getLessonClassKey: (className: string, branch?: string, curriculum?: string) => string
  isSlotDeleted: (classKey: string, subjectName: string) => boolean
  activeFilters: {branch?: string; level?: string; curriculum?: string}
  defaultFrequency: string
  defaultDuration: string
  /** Called every time AI allocations actually land on the grid (apply/auto-apply). */
  onApplied?: () => void
}

const useMock =
  String(import.meta.env.VITE_MOCK_ONBOARDING_AI_ALLOCATION ?? '').toLowerCase() === 'true'

type RunAiRequestOptions = {
  conversationHistoryOverride?: ConversationTurn[]
  autoApply?: boolean
}

export function useTeacherAllocationAi(params: UseTeacherAllocationAiParams) {
  const {
    manualDataRef,
    lessonPlanningData,
    setLessonPlanningData,
    normalizeBranchName,
    getSubjectsForClass,
    getLessonClassKey,
    isSlotDeleted,
    activeFilters,
    defaultFrequency,
    defaultDuration,
    onApplied,
  } = params

  const dispatch = useDispatch()
  const intl = useIntl()

  // Ref so a new callback identity from the parent does not churn commitActions.
  const onAppliedRef = useRef(onApplied)
  onAppliedRef.current = onApplied

  const urddId = useSelector<RootState>((state) => state.userReducer.urddId) as string | null
  const tenant = useSelector<RootState>((state) => state.userReducer.tenant) as string
  const storageKey = useMemo(() => buildAllocationAiSessionKey(urddId, tenant), [tenant, urddId])

  // Read once during the first render (not in an effect) so that returning to this step
  // paints the chat and the correct canClearAll/canRevert toolbar state immediately.
  const [restored] = useState(() => readAllocationAiSession(storageKey))

  // A live preview is deliberately not persisted: it is an un-applied proposal built
  // against a context that may be stale by the time the user comes back.
  const [preview, setPreview] = useState<AiAllocationResponse | null>(null)
  const [lastContext, setLastContext] = useState<AiAllocationContext | null>(null)
  const [conversationHistory, setConversationHistory] = useState<ConversationTurn[]>(
    restored?.conversationHistory ?? []
  )
  const [isLoading, setIsLoading] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [scope, setScope] = useState<AllocationScope>(restored?.scope ?? 'active_filters')
  const [applyCount, setApplyCount] = useState(restored?.applyCount ?? 0)
  // Slots whose changes were just applied (green flash) or whose pending
  // proposal was just discarded (red flash) — the grid highlights them for a
  // few seconds so the user can spot them. Setting one clears the other:
  // only the latest action flashes.
  const [appliedSlotKeys, setAppliedSlotKeys] = useState<Set<string>>(new Set())
  const appliedFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [discardedSlotKeys, setDiscardedSlotKeys] = useState<Set<string>>(new Set())
  const discardedFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastUserPromptRef = useRef('')
  const pendingActionsRef = useRef<AiAllocationAction[] | null>(null)
  const baselineGridRef = useRef<LessonPlanningData | null>(restored?.baselineGrid ?? null)
  const undoStackRef = useRef<LessonPlanningData[]>(restored?.undoStack ?? [])

  // Bumped whenever a ref-held part of the session changes, to trigger a persist.
  const [sessionRevision, setSessionRevision] = useState(0)
  // The key we are currently reading/writing. Held as state (not a ref) so that adopting
  // a new key batches with the restored values, letting the persist effect below run
  // exactly once against a consistent key/state pair.
  const [activeKey, setActiveKey] = useState(storageKey)

  const persistSession = useCallback(() => setSessionRevision((r) => r + 1), [])

  useEffect(() => {
    return () => {
      if (appliedFlashTimerRef.current) clearTimeout(appliedFlashTimerRef.current)
      if (discardedFlashTimerRef.current) clearTimeout(discardedFlashTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (storageKey === activeKey) return

    // urddId is fetched asynchronously after login (see App.tsx), so the first render
    // can land on the "anon" key and only learn the real one a moment later.
    const hasLiveSession =
      conversationHistory.length > 0 || applyCount > 0 || Boolean(baselineGridRef.current)

    if (hasLiveSession) {
      // Carry the in-flight session across to the properly scoped key.
      clearAllocationAiSession(activeKey)
      setActiveKey(storageKey)
      return
    }

    const next = readAllocationAiSession(storageKey)
    baselineGridRef.current = next?.baselineGrid ?? null
    undoStackRef.current = next?.undoStack ?? []
    setConversationHistory(next?.conversationHistory ?? [])
    setApplyCount(next?.applyCount ?? 0)
    setScope(next?.scope ?? 'active_filters')
    setActiveKey(storageKey)
  }, [activeKey, applyCount, conversationHistory.length, storageKey])

  useEffect(() => {
    // Nothing to save yet - skip the write, but never delete here. This effect also runs
    // on a fresh mount, so deleting would destroy a previously saved session any time the
    // restore came back empty (corrupt blob, schema bump, key not resolved yet). Clearing
    // is an explicit user action only - see startNewChat.
    if (conversationHistory.length === 0 && applyCount === 0 && !baselineGridRef.current) {
      return
    }

    writeAllocationAiSession(activeKey, {
      conversationHistory,
      baselineGrid: baselineGridRef.current,
      undoStack: undoStackRef.current,
      applyCount,
      scope,
    })
    // sessionRevision is a change signal for the refs above, not a value we read.
  }, [activeKey, applyCount, conversationHistory, scope, sessionRevision])

  const buildContext = useCallback(
    (scopeValue: AllocationScope) =>
      buildAllocationContext({
        manualData: manualDataRef.current,
        lessonPlanningData,
        scope: scopeValue,
        activeFilters,
        normalizeBranchName,
        getSubjectsForClass,
        getLessonClassKey,
        isSlotDeleted,
      }),
    [
      activeFilters,
      getLessonClassKey,
      getSubjectsForClass,
      isSlotDeleted,
      lessonPlanningData,
      manualDataRef,
      normalizeBranchName,
    ]
  )

  const executeAllocationApi = useCallback(
    (requestBody: AiAllocationRequest) =>
      new Promise<AiAllocationResponse>((resolve, reject) => {
        if (useMock) {
          resolve(
            buildMockAllocationResponse(requestBody.context, requestBody.prompt, {
              confirmOverwrite: requestBody.confirmOverwrite,
              pendingActions: requestBody.pendingActions,
            })
          )
          return
        }

        dispatch(
          requestOnboardingAiAllocation(
            requestBody,
            (res: any) => resolve(normalizeAllocationAiResponse(res)),
            (err: any) =>
              reject(
                new Error(
                  err?.message ||
                    intl.formatMessage({
                      id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.ERROR_GENERIC',
                    })
                )
              )
          )
        )
      }),
    [dispatch, intl]
  )

  const commitActions = useCallback(
    (
      actions: AiAllocationAction[],
      context: AiAllocationContext,
      currentGrid: LessonPlanningData
    ) => {
      if (actions.length === 0) return

      if (baselineGridRef.current === null) {
        baselineGridRef.current = cloneLessonPlanningData(currentGrid)
      }
      undoStackRef.current.push(cloneLessonPlanningData(currentGrid))
      // Bound the stack so persisting it cannot blow the sessionStorage quota.
      if (undoStackRef.current.length > MAX_UNDO_SNAPSHOTS) {
        undoStackRef.current = undoStackRef.current.slice(-MAX_UNDO_SNAPSHOTS)
      }

      const nextGrid = mapActionsToLessonPlanningData({
        actions,
        context,
        lessonPlanningData: currentGrid,
        manualData: manualDataRef.current,
        defaultFrequency,
        defaultDuration,
      })
      setLessonPlanningData(nextGrid)
      setApplyCount((c) => c + 1)
      persistSession()
      onAppliedRef.current?.()
    },
    [defaultDuration, defaultFrequency, manualDataRef, persistSession, setLessonPlanningData]
  )

  const storePendingConfirmation = useCallback((response: AiAllocationResponse) => {
    const pending = response.confirmation?.pendingActions ?? null
    pendingActionsRef.current = pending && pending.length > 0 ? pending : null
  }, [])

  const clearPendingConfirmation = useCallback(() => {
    pendingActionsRef.current = null
  }, [])

  const runAiRequest = useCallback(
    async (prompt: string, scopeValue: AllocationScope, options?: RunAiRequestOptions) => {
      setError(null)
      setIsLoading(true)

      const trimmedPrompt = prompt.trim()
      if (!options?.conversationHistoryOverride) {
        lastUserPromptRef.current = trimmedPrompt
      }

      const historyBase = options?.conversationHistoryOverride ?? conversationHistory
      const userTurn: ConversationTurn = {role: 'user', content: trimmedPrompt}
      const historyWithUser = [...historyBase, userTurn]
      setConversationHistory(historyWithUser)

      try {
        const context = buildContext(scopeValue)

        if (context.subjects.length === 0) {
          throw new Error(
            intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.ERROR_NO_SUBJECTS',
            })
          )
        }

        if (context.teachers.length === 0) {
          throw new Error(
            intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.ERROR_NO_TEACHERS',
            })
          )
        }

        const requestBody: AiAllocationRequest = {
          prompt: trimmedPrompt,
          context,
          conversationHistory: historyBase,
        }

        const response = await executeAllocationApi(requestBody)

        setLastContext(context)
        storePendingConfirmation(response)

        const assistantTurn: ConversationTurn = {
          role: 'assistant',
          content: response.previewMessage,
        }
        const historyWithAssistant = [...historyWithUser, assistantTurn]

        const effectiveActions = getEffectiveAllocationActions(response)
        const hasActionablePreview = effectiveActions.length > 0 || Boolean(response.disambiguation)

        if (
          options?.autoApply &&
          effectiveActions.length > 0 &&
          !response.disambiguation &&
          !hasAllocationConfirmation(response)
        ) {
          commitActions(effectiveActions, context, lessonPlanningData)
          setConversationHistory(historyWithAssistant)
          setPreview(null)
          clearPendingConfirmation()
          toast.success(
            intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.APPLY_SUCCESS',
            })
          )
          return
        }

        setConversationHistory(historyWithAssistant)
        if (hasActionablePreview) {
          setPreview(response)
        } else {
          setPreview(null)
          setLastContext(null)
          clearPendingConfirmation()
        }
      } catch (e: any) {
        const message =
          e?.message ||
          intl.formatMessage({
            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.ERROR_GENERIC',
          })
        setError(message)
        toast.error(message)
      } finally {
        setIsLoading(false)
      }
    },
    [
      buildContext,
      clearPendingConfirmation,
      commitActions,
      conversationHistory,
      executeAllocationApi,
      intl,
      lessonPlanningData,
      storePendingConfirmation,
    ]
  )

  const askAi = useCallback(
    (prompt: string, scopeOverride?: AllocationScope) => {
      const trimmed = prompt.trim()
      if (!trimmed || isLoading || preview) return
      const scopeValue = scopeOverride ?? scope
      runAiRequest(trimmed, scopeValue)
    },
    [isLoading, preview, runAiRequest, scope]
  )

  const cancelPreview = useCallback(() => {
    // Flash the slots the discarded proposal was targeting, in red, so the
    // user sees exactly what was thrown away.
    if (preview) {
      const targetedKeys = getPreviewSlotKeys(getEffectiveAllocationActions(preview))
      if (targetedKeys.size > 0) {
        if (appliedFlashTimerRef.current) clearTimeout(appliedFlashTimerRef.current)
        if (discardedFlashTimerRef.current) clearTimeout(discardedFlashTimerRef.current)
        setAppliedSlotKeys(new Set())
        setDiscardedSlotKeys(targetedKeys)
        discardedFlashTimerRef.current = setTimeout(() => setDiscardedSlotKeys(new Set()), 3500)
      }
    }
    setPreview(null)
    setLastContext(null)
    setError(null)
    clearPendingConfirmation()
  }, [clearPendingConfirmation, preview])

  const resolveDisambiguation = useCallback(
    (option: AiDisambiguationOption) => {
      if (!preview?.disambiguation || isLoading || isApplying) return

      const followUp = buildDisambiguationFollowUp(
        lastUserPromptRef.current,
        preview.disambiguation.matchedName,
        option.employeeId
      )

      setPreview(null)
      runAiRequest(followUp, scope, {
        conversationHistoryOverride: conversationHistory,
        autoApply: true,
      })
    },
    [conversationHistory, isApplying, isLoading, preview, runAiRequest, scope]
  )

  const applyPreview = useCallback(async () => {
    if (!preview || !lastContext || isApplying || preview.disambiguation) return

    const previewActions = getEffectiveAllocationActions(preview)
    if (previewActions.length === 0) return

    const needsConfirmApi = hasAllocationConfirmation(preview)
    const pendingActions =
      preview.confirmation?.pendingActions ?? pendingActionsRef.current ?? previewActions

    setIsApplying(true)
    setError(null)

    try {
      const context = buildContext(scope)
      let actionsToApply = previewActions

      if (needsConfirmApi) {
        const response = await executeAllocationApi({
          prompt: 'yes',
          context,
          conversationHistory,
          confirmOverwrite: true,
          pendingActions,
        })

        actionsToApply = getEffectiveAllocationActions(response)
        if (actionsToApply.length === 0) {
          throw new Error(
            intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.ERROR_GENERIC',
            })
          )
        }
      }

      commitActions(actionsToApply, context, lessonPlanningData)
      if (appliedFlashTimerRef.current) clearTimeout(appliedFlashTimerRef.current)
      if (discardedFlashTimerRef.current) clearTimeout(discardedFlashTimerRef.current)
      setDiscardedSlotKeys(new Set())
      setAppliedSlotKeys(getPreviewSlotKeys(actionsToApply))
      appliedFlashTimerRef.current = setTimeout(() => setAppliedSlotKeys(new Set()), 3500)
      setPreview(null)
      setLastContext(null)
      clearPendingConfirmation()
      toast.success(
        intl.formatMessage({
          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.APPLY_SUCCESS',
        })
      )
    } catch (e: any) {
      const message =
        e?.message ||
        intl.formatMessage({
          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.ERROR_GRID_SYNC',
        })
      setError(message)
      toast.error(message)
    } finally {
      setIsApplying(false)
    }
  }, [
    buildContext,
    clearPendingConfirmation,
    commitActions,
    conversationHistory,
    executeAllocationApi,
    intl,
    isApplying,
    lastContext,
    lessonPlanningData,
    preview,
    scope,
  ])

  const resetAiSession = useCallback(() => {
    setConversationHistory([])
    setPreview(null)
    setLastContext(null)
    setError(null)
    lastUserPromptRef.current = ''
    clearPendingConfirmation()
    baselineGridRef.current = null
    undoStackRef.current = []
    setApplyCount(0)
    persistSession()
  }, [clearPendingConfirmation, persistSession])

  /**
   * "Start new chat" - drops the conversation and the undo/baseline history without
   * touching the grid, so allocations the user already applied stay in place.
   */
  const startNewChat = useCallback(() => {
    if (isLoading || isApplying) return
    resetAiSession()
    clearAllocationAiSession(activeKey)
  }, [activeKey, isApplying, isLoading, resetAiSession])

  const clearAllGridChanges = useCallback(() => {
    if (applyCount === 0 || !baselineGridRef.current) return
    if (isLoading || isApplying || preview) return

    setLessonPlanningData(cloneLessonPlanningData(baselineGridRef.current))
    undoStackRef.current = []
    setApplyCount(0)
    setPreview(null)
    setLastContext(null)
    setError(null)
    clearPendingConfirmation()
    persistSession()
  }, [
    applyCount,
    clearPendingConfirmation,
    isApplying,
    isLoading,
    persistSession,
    preview,
    setLessonPlanningData,
  ])

  const revertPreviousGridChange = useCallback(() => {
    if (undoStackRef.current.length === 0) return
    if (isLoading || isApplying || preview) return

    const previous = undoStackRef.current.pop()
    if (!previous) return

    setLessonPlanningData(cloneLessonPlanningData(previous))
    setApplyCount((c) => Math.max(0, c - 1))
    persistSession()
  }, [isApplying, isLoading, persistSession, preview, setLessonPlanningData])

  const previewSlotKeys = useMemo(() => {
    if (!preview) return new Set<string>()
    return getPreviewSlotKeys(getEffectiveAllocationActions(preview))
  }, [preview])

  // slotKey -> incoming teacher name for pending "assign"/"replace" actions, so
  // the grid can preview the teacher who will take over the slot.
  const previewIncomingTeachers = useMemo(() => {
    const map = new Map<string, string>()
    if (!preview) return map
    getEffectiveAllocationActions(preview).forEach((action) => {
      if (action.type === 'unassign') return
      const employeeId = String(action.teacherEmployeeId ?? '').trim()
      const name =
        String(action.teacherName ?? '').trim() ||
        lastContext?.teachers.find((t) => t.employeeId === employeeId)?.name ||
        employeeId
      if (name) map.set(action.slotKey, name)
    })
    return map
  }, [preview, lastContext])

  const hasActionPreview = Boolean(
    preview && (getEffectiveAllocationActions(preview).length > 0 || preview.disambiguation)
  )
  const isBusy = isLoading || isApplying || Boolean(preview)
  // Recomputed on every session mutation; the stack can be shallower than applyCount
  // once older snapshots are shed by the MAX_UNDO_SNAPSHOTS cap.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const undoDepth = useMemo(() => undoStackRef.current.length, [sessionRevision])
  const canClearAll = applyCount > 0 && Boolean(baselineGridRef.current) && !isBusy
  const canRevert = undoDepth > 0 && !isBusy
  const canStartNewChat =
    (conversationHistory.length > 0 || applyCount > 0) && !isLoading && !isApplying

  return {
    preview,
    lastContext,
    conversationHistory,
    isLoading,
    isApplying,
    error,
    scope,
    setScope,
    askAi,
    applyPreview,
    cancelPreview,
    resolveDisambiguation,
    resetAiSession,
    startNewChat,
    clearAllGridChanges,
    revertPreviousGridChange,
    canClearAll,
    canRevert,
    canStartNewChat,
    previewSlotKeys,
    previewIncomingTeachers,
    appliedSlotKeys,
    discardedSlotKeys,
    hasActionPreview,
  }
}
